import { useMutation } from '@tanstack/react-query';
import PQueue from 'p-queue';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { throttle } from 'underscore';

import { WebApi } from '../../../../services/web-api';
import { CloudFrontSigner } from '../../../../utils/cloud-front-signer/cloud-front-signer';

export type UploadImageParameters = {
  imageIdentifier: string;
  file: File;
  signer: CloudFrontSigner;
  onSuccess: () => void;
  onError: () => void;
};

/**
 * Note: Most modern browsers can handle 6-8 concurrent uploads
 */
const defaultConcurrentUploads = 6;
/**
 * The least amount of concurrent uploads to allow.
 * The lower the more stable at the cost of increased upload time.
 */
const minConcurrentUploads = 1;
/**
 * Backoff interval. Ensuring that if many images fail to upload within a short
 * time frame the concurrency wont decrease several steps suddenly.
 */
const backoffThrottleIntervalMs = 3000;
const maxUploadAttempts = 3;

async function putImageToProcess(
  params: UploadImageParameters & { throttledBackoff: () => void },
  attempt = 1,
) {
  // Use last modified in conjunction with the filename to make sure it is a unique key.
  try {
    const signedRequest = params.signer.getSignedRequest(
      `upload/${params.imageIdentifier}/image_to_process`,
    );
    await WebApi.put(signedRequest, params.file);
  } catch (err) {
    if (attempt > maxUploadAttempts) {
      throw err;
    }

    params.throttledBackoff();
    await putImageToProcess(params, attempt + 1);
  }
}

/**
 * Queues images for upload. It will limit how many files are PUT at any one given time and
 * keep queuing them until all the images have been uploaded.
 */
export function useUploadImageQueue() {
  const pQueue = useRef(
    new PQueue({
      concurrency: defaultConcurrentUploads,
      autoStart: true,
    }),
  );

  const throttledBackoff = useMemo(
    () =>
      throttle(() => {
        const currentValue = pQueue.current.concurrency;
        pQueue.current.concurrency =
          currentValue > minConcurrentUploads ? currentValue - 1 : currentValue;
      }, backoffThrottleIntervalMs),
    [pQueue],
  );

  const { mutateAsync } = useMutation({
    mutationFn: putImageToProcess,
    onError: (error, variables) => {
      console.log(`${error.name}: ${error.message}`);
      variables.onError();
    },
    onSuccess: (_, variables) => {
      variables.onSuccess();
    },
  });

  const queueUpload = useCallback(
    (upload: UploadImageParameters) =>
      pQueue.current.add(
        async () =>
          await mutateAsync({
            ...upload,
            throttledBackoff,
          }),
      ),
    [mutateAsync, throttledBackoff],
  );

  // Ensure the queue is cleared when the context goes out of scope.
  useEffect(() => {
    const queue = pQueue.current;
    return () => queue.clear();
  }, [pQueue]);

  return queueUpload;
}
